import { Button, Col, Container, Form, Row, Spinner, Table } from "react-bootstrap";
import Paginations from "../../components/pagination";
import { toast } from "react-toastify";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import DeleteConfirmationModal from "../../components/delete-confirmation-modal";
import { getSettingData, isContentTypeSimilar } from "../../shared/scribe";
import ScribeContentModal from "../../components/scribe-content-modal";
import Breadcrumbs from "../../components/breadcrumbs";
import { isAdminRole } from "../../shared/utils";
import { useAccess } from "../../contexts/AccessContext";

const ScribeContents = () => {
  const [tableData, setTableData] = useState([]);
  const [paginationData, setPaginationData] = useState([]);
  const [page, setPage] = useState(1);
  const [showSpinner, setShowSpinner] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewId, setViewId] = useState();

  const [contentTypes, setContentTypes] = useState([]);
  const [contentType, setContentType] = useState("");
  const [contentTypeId, setContentTypeId] = useState("");
  const [createdOn, setCreatedOn] = useState("");
  const [title, setTitle] = useState("");
  const [debouncedTitle, setDebouncedTitle] = useState("");
  const scribeContentsToolId = 3;
  const { hasEdit, canSaveEdit } = useAccess();
  const isAdmin = useMemo(() => isAdminRole(), []);

  const [retryingIds, setRetryingIds] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);

  // Refs to track current values without causing re-renders
  const retryingIdsRef = useRef([]);
  const tableDataRef = useRef([]);
  const pollingTimeoutRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTitle(title);
    }, 500);

    return () => clearTimeout(timer);
  }, [title]);

  const loadItems = useCallback(
    (skipPolling = false, forceLoad = false) => {
      // Clear existing timeout to avoid multiple polling
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      if (retryingIdsRef.current.length === 0 || forceLoad) {
        setShowSpinner(true);
      }
      if (!skipPolling) {
        setSelectedIds([]);
      }

      const url = `${process.env.REACT_APP_API_URL}/scribe-bot`;
      const params = new URLSearchParams({
        page,
        content_type_id: contentTypeId ?? "",
        created_on: createdOn ?? "",
        title: title?.trim() ?? "",
      });

      axios
        .get(`${url}?${params.toString()}`, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        })
        .then((response) => {
          const data = response.data.data || [];
          // console.log("page", response.data.page_data);
          setPaginationData(
            response.data.page_data ?? {
              current_page: 1,
              page_size: 0,
              total_page: 1,
              total_records: 0,
            },
          );

          const inProgressIds = data
            .filter((row) => row.status === "GENERATION_INPROGRESS")
            .map((row) => row.id);

          if (inProgressIds.length > 0) {
            if (retryingIdsRef.current.length === 0 || forceLoad) {
              // No retrying IDs or force load, so set data directly
              setTableData(data);
              tableDataRef.current = data;
            } else {
              // Check if any of the retrying IDs have status changes
              const hasChanges = retryingIdsRef.current.some((id) => {
                const oldItem = tableDataRef.current.find((item) => item.id === id);
                const newItem = data.find((item) => item.id === id);
                return oldItem && newItem && oldItem.status !== newItem.status;
              });

              if (hasChanges || forceLoad) {
                // Update table data only if there are status changes or force load
                setTableData(data);
                tableDataRef.current = data;
              }
            }

            // Update retryingIds and its ref
            setRetryingIds(inProgressIds);
            retryingIdsRef.current = inProgressIds;

            // Continue polling for in-progress items with skipPolling=true to avoid clearing selectedIds
            pollingTimeoutRef.current = setTimeout(() => {
              loadItems(true);
            }, 10 * 1000);
          } else {
            // No in-progress items, update data normally
            setTableData(data);
            tableDataRef.current = data;
            setRetryingIds(inProgressIds);
            retryingIdsRef.current = inProgressIds;
          }
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          toast.error("Error fetching data");
        })
        .finally(() => {
          setShowSpinner(false);
        });
    },
    [page, contentTypeId, createdOn, debouncedTitle],
  );

  // Handle page changes
  const handlePageChange = (newPage) => {
    // Clear any ongoing polling when user changes page
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    setPage(newPage);
  };

  const [triggerReload, setTriggerReload] = useState(false);
  useEffect(() => {
    if (triggerReload) {
      loadItems();
      setTriggerReload(false);
    }
  }, [triggerReload, loadItems]);

  // Load data when page changes (excluding first load)
  useEffect(() => {
    if (page) {
      loadItems(false, true); // Force load when page changes
    }
  }, [page, loadItems]);

  const [downloadingType, setDownloadingType] = useState();
  const downloadItems = (type) => {
    setDownloadingType(type);
    const url = `${process.env.REACT_APP_API_URL}/scribe-bot`;
    const params = new URLSearchParams(
      selectedIds?.length
        ? {
            ids: JSON.stringify(selectedIds),
            output_format: type,
          }
        : {
            output_format: type,
            content_type_id: contentTypeId ?? "",
            created_on: createdOn ?? "",
            title: title?.trim() ?? "",
            ids: `[${viewId}]`,
          },
    );
    axios
      .put(`${url}?${params.toString()}`, undefined, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then(({ data }) => {
        const fileName = data.data;
        const fileUrl = `https://svhs-scribe-ai.s3.us-east-2.amazonaws.com/${fileName}`;
        fetch(fileUrl, { method: "GET" })
          .then((response) => response.blob())
          .then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
          });
      })
      .catch((error) => {
        console.error("Error downloading file:", error);
        toast.error("Error downloading file");
      })
      .finally(() => {
        setDownloadingType(false);
      });
  };

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deleteSelectedItems = () => {
    setDeleting(true);
    const url = `${process.env.REACT_APP_API_URL}/scribe-bot`;
    const params = new URLSearchParams({
      ids: JSON.stringify(selectedIds),
    });
    const remainingItemsAfterDelete = tableData.length - selectedIds.length;
    const shouldGoToPreviousPage = remainingItemsAfterDelete === 0 && page > 1;

    axios
      .delete(`${url}?${params.toString()}`, undefined, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then(({ data }) => {
        if (shouldGoToPreviousPage) {
          setPage(page - 1);
        } else {
          loadItems();
        }
      })
      .catch((error) => {
        console.error("Error downloading file:", error);
        toast.error("Error downloading file");
      })
      .finally(() => {
        setDeleting(false);
        setDeleteModal(false);
        toast.success("Deleted successfully");
      });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(tableData.map((row) => row.id));
      setIsSelectAll(true);
    } else {
      setSelectedIds([]);
      setIsSelectAll(false);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds((prev) => {
      const clickedRow = tableData.find((row) => row.id === id);

      if (prev.includes(id)) {
        // If unchecking and "Select All" was checked, unselect all items of the same type
        if (isSelectAll) {
          const newSelected = prev.filter((selectedId) => {
            const row = tableData.find((r) => r.id === selectedId);
            return !isContentTypeSimilar(row?.is_question, clickedRow?.is_question);
          });
          setIsSelectAll(false);
          return newSelected;
        } else {
          // Normal uncheck behavior
          const newSelected = prev.filter((selectedId) => selectedId !== id);
          if (newSelected.length < tableData.length) {
            setIsSelectAll(false);
          }
          return newSelected;
        }
      } else {
        const newSelected = [...prev, id];
        if (newSelected.length === tableData.length) {
          setIsSelectAll(true);
        }
        return newSelected;
      }
    });
  };

  const selectedData = useMemo(() => tableData?.find((d) => d.id === viewId), [tableData, viewId]);

  const [publishing, setPublishing] = useState();
  const publishPosts = (ids) => {
    setPublishing(ids.length === 1 ? ids[0] : true);
    const url = `${process.env.REACT_APP_API_URL}/scribe-bot-publish`;
    axios
      .post(
        url,
        { ids },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      )
      .then(({ data }) => {
        loadItems();
      })
      .catch((error) => {
        console.error("Error publishing post:", error);
        toast.error("Error publishing post");
      })
      .finally(() => {
        setPublishing(false);
      });
  };

  const retry = (ids) => {
    setRetryingIds(ids);
    retryingIdsRef.current = ids;
    const url = `${process.env.REACT_APP_API_URL}/scribe-bot-publish`;
    const params = new URLSearchParams({ ids: `[${ids}]` });
    axios
      .get(`${url}?${params.toString()}`, undefined, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then(({ data }) => {
        setTimeout(() => {
          setRetryingIds((prev) => {
            const newRetryingIds = prev.filter((id) => !ids.includes(id));
            retryingIdsRef.current = newRetryingIds;
            return newRetryingIds;
          });
          loadItems();
        }, 10 * 1000); // wait for 10 seconds before reloading
      })
      .catch((error) => {
        console.error("Error retrying:", error);
        toast.error("Error retrying");
      });
  };

  useEffect(() => {
    getSettingData()
      .then((data) => {
        setContentTypes(data);
      })
      .catch((e) => {
        console.error(e.message);
      });
  }, []);

  const hasFilters = useMemo(() => {
    return contentType || createdOn || title.trim();
  }, [contentType, createdOn, title]);

  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Container fluid className="p-4">
      <Breadcrumbs />
      <p className="section-title mb-5">Generated Contents</p>
      <Row className="align-items-end">
        <Col md="4">
          <Form.Group className="text-left mb-2">
            <Form.Label style={{ width: "100%" }}>Content type</Form.Label>
            <Form.Select
              value={contentType}
              onChange={(e) => {
                setContentType(e.target.value);
                const filteredData = contentTypes.find((m) => m.content_type === e.target.value);
                setContentTypeId(filteredData.id);
                setPage(1);
              }}
            >
              <option value="" disabled>
                -- Please choose an option --
              </option>
              {contentTypes &&
                contentTypes.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.content_type}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md="4">
          <Form.Group className="text-left mb-2">
            <Form.Label style={{ width: "100%" }}>Title</Form.Label>
            <Form.Control
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setPage(1); // reset page
              }}
            />
          </Form.Group>
        </Col>
        <Col md="4">
          <Form.Group className="text-left mb-2">
            <Form.Label style={{ width: "100%" }}>Created on</Form.Label>
            <Form.Control
              type="date"
              value={createdOn}
              onChange={(e) => {
                setCreatedOn(e.target.value);
                setPage(1);
              }}
            />
          </Form.Group>
        </Col>
      </Row>
      <div style={{ display: "flex", gap: "8px" }}>
        <Button
          onClick={() => {
            setPage(1);
            loadItems();
          }}
          className="mb-2"
        >
          Search
        </Button>
        {selectedIds.length > 0 &&
          selectedIds.every((id) => tableData.find((d) => d.id === id)?.is_question == 1) && (
            <Button
              disabled={!!downloadingType}
              onClick={() => downloadItems("xml")}
              className="mb-2"
            >
              {downloadingType === "xml" ? "Downloading" : "Download as XML"}
            </Button>
          )}

        {selectedIds.length > 0 &&
          selectedIds.every((id) => tableData.find((d) => d.id === id)?.is_question == 0) && (
            <Button
              disabled={!!downloadingType}
              onClick={() => downloadItems("csv")}
              className="mb-2"
            >
              {downloadingType === "csv" ? "Downloading" : "Download as CSV"}
            </Button>
          )}
        <Button
          variant="outline-dark"
          onClick={() => {
            setSelectedIds([]);
            setIsSelectAll(false);
            setContentType("");
            setCreatedOn("");
            setTitle("");
            setDebouncedTitle("");
            setTriggerReload(true);
            setContentTypeId();
            setPage(1);
          }}
          className="mb-2"
          disabled={!hasFilters && selectedIds.length === 0}
        >
          Clear
        </Button>
        {!!selectedIds.length && (
          <>
            <Button
              variant="outline-dark"
              onClick={() => {
                setDeleteModal(true);
              }}
              className="mb-2"
              disabled={!!deleting}
            >
              {deleting ? "Deleting" : "Delete"}
            </Button>
            {isAdmin &&
              selectedIds.length > 0 &&
              selectedIds.every((id) => tableData.find((d) => d.id === id)?.is_question == 0) && (
                <Button
                  variant="outline-dark"
                  onClick={() => publishPosts(selectedIds)}
                  className="mb-2"
                  disabled={publishing == true}
                >
                  {publishing ? "Publishing" : "Publish"}
                </Button>
              )}
          </>
        )}
      </div>
      <Row>
        <Table responsive className="scribe-contents-table">
          <thead>
            <tr>
              <th>
                <Form.Check
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedIds.length && selectedIds.length === tableData?.length}
                />
              </th>
              <th>Title</th>
              <th>Content Type</th>
              <th>Generated By</th>
              <th>Created On</th>
              {(isAdmin || hasEdit(scribeContentsToolId)) && <th>Wordpress Post</th>}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {showSpinner ? (
              <tr>
                <td
                  colSpan={isAdmin ? "7" : "6"}
                  style={{ textAlign: "center", padding: "4rem 0" }}
                >
                  <Button
                    hidden={!showSpinner}
                    variant="link"
                    className="mt-2 mb-5 btn-spinner ms-0 text-decoration-none"
                    disabled
                  >
                    <Spinner
                      as="span"
                      animation="grow"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-1"
                    />
                  </Button>{" "}
                </td>
              </tr>
            ) : tableData?.length > 0 ? (
              tableData.map((row) => (
                <tr key={row.id + "key"}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selectedIds.includes(row.id)}
                      disabled={
                        !isSelectAll &&
                        selectedIds.length &&
                        !isContentTypeSimilar(
                          tableData.find((d) => d.id === selectedIds[0])?.is_question,
                          row.is_question,
                        )
                      }
                      onChange={() => handleSelectRow(row.id)}
                    />
                  </td>
                  <td>{row.title}</td>
                  <td>
                    {contentTypes.find((ct) => ct.content_type === row.content_type)
                      ?.content_type ?? row.content_type}
                  </td>
                  <td>{row.fullname}</td>
                  <td>{new Date(row.created_on).toLocaleString()}</td>
                  {(isAdmin || hasEdit(scribeContentsToolId)) &&  (
                    <td>
                      {row.is_question == 0 && row.status === "VALIDATION_COMPLETED" ? (
                        row.wordpress_site ? (
                          <a href={row.wordpress_site} target="_blank">
                            Open
                          </a>
                        ) : publishing === row.id ? (
                          "Publishing"
                        ) : (
                          <a href="#" onClick={() => publishPosts([row.id])}>
                            Publish
                          </a>
                        )
                      ) : row.is_question == 1 ? (
                        "N/A"
                      ) : (
                        <></>
                      )}
                    </td>
                  )}
                  <td>
                    <div className="d-flex justify-content-end">
                      {retryingIds.includes(row.id) || row.status === "GENERATION_INPROGRESS" ? (
                        <div style={{ width: "24px", textAlign: "center" }}>
                          <img src="/images/loader.svg" style={{ width: "20px" }} />
                        </div>
                      ) : ["GENERATION_ERROR", "VALIDATION_ERROR"].includes(row.status) ? (
                        <img
                          style={{
                            cursor: "pointer",
                            height: "18px",
                            marginRight: "4px",
                          }}
                          src="/images/refresh-cw.svg"
                          alt="retry"
                          onClick={() => retry([row.id])}
                        />
                      ) : (
                        <img
                          style={{ cursor: "pointer" }}
                          src="/images/eye-grey.svg"
                          alt=""
                          onClick={() => setViewId(row.id)}
                        />
                      )}
                      {(isAdmin || hasEdit(scribeContentsToolId)) &&  (
                      <img
                        style={{ cursor: "pointer", marginLeft: "8px" }}
                        src={"/images/delete-grey.svg"}
                        alt="trash"
                        onClick={() => {
                          setSelectedIds([row.id]);
                          setDeleteModal(true);
                        }}
                      />
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : !showSpinner ? (
              <tr>
                <td colSpan={isAdmin ? "7" : "6"} style={{ textAlign: "center", padding: "2rem" }}>
                  <img src="/images/no-data-found.jpeg" style={{ maxWidth: "400px" }} />
                  <p>Data will be updated soon...</p>
                </td>
              </tr>
            ) : null}
          </tbody>
        </Table>
      </Row>
      {tableData?.length > 0 && !showSpinner && (
        <Row>
          <Col className="d-flex justify-content-start disabled">
            Displaying {tableData.length} of {paginationData.total_records} Records
          </Col>
          <Col className="d-flex justify-content-end">
            <Paginations
              current_page={paginationData.current_page}
              page_size={paginationData.page_size}
              total_page={paginationData.total_page}
              total_records={paginationData.total_records}
              clicked_page={handlePageChange}
            />
          </Col>
        </Row>
      )}
      <ScribeContentModal
        show={!!viewId}
        onHide={() => setViewId()}
        data={selectedData}
        onDownload={(type) => {
          setSelectedIds([viewId]);
          setTimeout(() => downloadItems(type));
        }}
        isDownloading={!!downloadingType}
        onContentSave={(editContent) =>
          setTableData((list) =>
            list.map((item) =>
              item.id === viewId ? { ...item, generated_content: editContent } : item,
            ),
          )
        }
        dataLoad={loadItems}
        isAdmin={isAdmin}
        hasEdit={hasEdit(scribeContentsToolId)}
        canSaveEdit={canSaveEdit(scribeContentsToolId)}
      />
      {!!deleteModal && selectedIds.length > 0 && (
        <DeleteConfirmationModal
          loading={deleting}
          onClose={() => {
            setDeleteModal(false);
            setSelectedIds([]);
          }}
          onConfirm={() => deleteSelectedItems()}
        />
      )}
    </Container>
  );
};

export default ScribeContents;
